import React, { useEffect, useState } from "react";
import { WhatsAppTemplate } from "../../types/api";
import apiService from "../../services/api";

interface PatientInfo {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  hm_id?: string;
  phone?: string;
}

interface WhatsAppTemplatePickerProps {
  onSelect: (body: string, templateId: string, variables?: Record<string, string>) => void;
  onClose: () => void;
  patient?: PatientInfo;
}

const PATIENT_FIELDS: Record<string, (p: PatientInfo) => string> = {
  first_name: (p) => p.first_name || "",
  last_name:  (p) => p.last_name  || "",
  full_name:  (p) => p.full_name  || `${p.first_name || ""} ${p.last_name || ""}`.trim(),
  hm_id:      (p) => p.hm_id     || "",
  phone:      (p) => p.phone      || "",
};

/** Extract named param names from {{param_name}} placeholders */
function extractNamedParams(text: string): string[] {
  const found = [...text.matchAll(/\{\{([^}]+)\}\}/g)].map((m) => m[1]);
  return [...new Set(found)];
}

/** Substitute {{param_name}} with values */
function resolveText(text: string, values: Record<string, string>): string {
  let result = text;
  for (const [name, val] of Object.entries(values)) {
    result = result.replaceAll(`{{${name}}}`, val || `{{${name}}}`);
  }
  return result;
}

export const WhatsAppTemplatePicker: React.FC<WhatsAppTemplatePickerProps> = ({ onSelect, onClose, patient }) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<WhatsAppTemplate | null>(null);
  // values keyed by param_name
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const token = localStorage.getItem("token") || "";
    apiService
      .getWhatsAppTemplates(token)
      .then(setTemplates)
      .finally(() => setIsLoading(false));
  }, []);

  function handleSelectTemplate(t: WhatsAppTemplate) {
    const initial: Record<string, string> = {};

    // Collect all unique param names from body + header
    const allParams = [
      ...extractNamedParams(t.body),
      ...extractNamedParams(t.header || ""),
    ].filter((v, i, a) => a.indexOf(v) === i);

    for (const paramName of allParams) {
      // Patient field takes priority, then param_defaults, then empty
      if (patient && paramName in PATIENT_FIELDS) {
        initial[paramName] = PATIENT_FIELDS[paramName](patient);
      } else {
        initial[paramName] = t.param_defaults?.[paramName] || "";
      }
    }

    setValues(initial);
    setSelected(t);
  }

  function handleSend() {
    if (!selected) return;
    const resolvedHeader = selected.header ? resolveText(selected.header, values) : "";
    const resolvedBody = resolveText(selected.body, values);
    // Display text: header (bold marker via newline) then body
    const displayText = resolvedHeader ? `${resolvedHeader}\n${resolvedBody}` : resolvedBody;

    // Send all params as param_name→value (WATI v3 custom_params format)
    const namedValues: Record<string, string> = {};
    for (const paramName of allParams) {
      namedValues[paramName] = values[paramName] ?? "";
    }

    onSelect(displayText, selected.id, namedValues);
  }

  const headerParams = selected ? extractNamedParams(selected.header || "") : [];
  const bodyParams   = selected ? extractNamedParams(selected.body) : [];
  // All unique params: body first, then header-only params
  const allParams = [...bodyParams, ...headerParams.filter((p) => !bodyParams.includes(p))];

  const canSend = selected && allParams.every((p) => (values[p] || "").trim() !== "");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 12,
          width: selected ? 500 : 420,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#3A7380",
            color: "#fff",
            padding: "14px 20px",
            borderRadius: "12px 12px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          {selected && (
            <button
              onClick={() => setSelected(null)}
              style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 18, padding: 0 }}
            >
              ←
            </button>
          )}
          <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>
            {selected ? selected.name : "תבניות הודעה מאושרות"}
          </span>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 18 }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {/* ── Template list ── */}
          {!selected && (
            <>
              {isLoading && (
                <div style={{ textAlign: "center", color: "#888", padding: 20 }}>טוען תבניות...</div>
              )}
              {!isLoading && templates.length === 0 && (
                <div style={{ textAlign: "center", color: "#888", padding: 20 }}>אין תבניות זמינות</div>
              )}
              {templates.map((t) => (
                <button
                  data-ai-id="whatsapp-template-option"
                  key={t.id}
                  onClick={() => handleSelectTemplate(t)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "right",
                    padding: "12px 14px",
                    marginBottom: 8,
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    background: "#f9f9f9",
                    cursor: "pointer",
                    direction: "rtl",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#e8f4f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#f9f9f9")}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#3A7380", marginBottom: 4 }}>
                    {t.name}
                  </div>
                  {t.header && (
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 2, fontStyle: "italic" }}>
                      כותרת: {t.header}
                    </div>
                  )}
                  <div style={{ fontSize: 13, color: "#444", lineHeight: 1.5 }}>{t.body}</div>
                </button>
              ))}
            </>
          )}

          {/* ── Param fill ── */}
          {selected && (
            <div style={{ direction: "rtl" }}>
              {/* Preview */}
              <div
                style={{
                  backgroundColor: "#dcf8c6",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 20,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "#111",
                }}
              >
                {selected.header && (
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {resolveText(selected.header, values)}
                  </div>
                )}
                <div style={{ whiteSpace: "pre-wrap" }}>{resolveText(selected.body, values)}</div>
              </div>

              {/* Param inputs */}
              {allParams.length === 0 && (
                <div style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
                  אין פרמטרים לתבנית זו.
                </div>
              )}
              {allParams.map((paramName) => {
                const isHeaderOnly = headerParams.includes(paramName) && !bodyParams.includes(paramName);
                const label = `${paramName}${isHeaderOnly ? " (כותרת)" : ""}`;
                return (
                  <div key={paramName} style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 12, color: "#555", marginBottom: 4 }}>
                      {label}
                    </label>
                    <input
                      type="text"
                      value={values[paramName] || ""}
                      onChange={(e) => setValues((prev) => ({ ...prev, [paramName]: e.target.value }))}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        fontSize: 13,
                        direction: "rtl",
                        boxSizing: "border-box",
                      }}
                      placeholder={`ערך עבור ${paramName}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Send button (param fill step only) */}
        {selected && (
          <div style={{ padding: "12px 16px", borderTop: "1px solid #eee" }}>
            <button
              onClick={handleSend}
              disabled={!canSend}
              style={{
                width: "100%",
                padding: "10px",
                backgroundColor: canSend ? "#3A7380" : "#aaa",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: canSend ? "pointer" : "default",
              }}
            >
              שלח הודעה
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
